
import { defineComponent } from "vue";
import CreateEditDialog from "@/components/controls/catalogueGrid/CreateEditDialog.vue";
import DxTextBox from "devextreme-vue/text-box";
import DxSelectBox from "devextreme-vue/select-box";
import IntegerControl from "@/components/controls/base/IntegerControl.vue";
import DecimalControl from "@/components/controls/base/DecimalControl.vue";
import mountComponentMixin from "@/mixins/MountComponentMixin";
import CatalogueSelectControl from "@/components/controls/base/CatalogueSelectControl.vue";
import DateControl from "@/components/controls/base/DateControl.vue";
import EnumControl from "@/components/controls/base/EnumControl.vue";
import { FeatureType } from "@/components/catalogues/FeatureCatalogue/FeatureType";
import { setDisplayedOperations, setOperation } from "./SetOperation";
import { getFeature } from "../../../api/featureApi";
import { getDCatalogue } from "../../../api/dCatalogueApi";

export default defineComponent({
  components: {
    CreateEditDialog,
    DxTextBox,
    DxSelectBox,
    CatalogueSelectControl,
    EnumControl,
    IntegerControl,
    DecimalControl,
    DateControl,
  },
  mixins: [mountComponentMixin],
  props: {
    saveModelCallback: { required: true },
  },
  watch: {
    catalogueId(newVal) {
      if (this.isCatalogue && newVal) this.onCatalogueChange();
    },
    enumId() {
      if (this.isEnum) this.onEnumChange();
    },
    dCatalogueId() {
      if (this.isDCatalogue) this.onDCatalogueChange();
    },
  },
  computed: {
    catalogueId(): any {
      return this.controlData.catalogueId;
    },
    enumId(): any {
      return this.controlData.enumId;
    },
    dCatalogueId(): any {
      return this.controlData.dCatalogueId;
    },
    modelId(): any {
      return this.modelLocal.id;
    },
    isBoolean(): any {
      return this.controlType == FeatureType.Boolean;
    },
    isInteger(): any {
      return this.controlType == FeatureType.Integer;
    },
    isDecimal(): any {
      return this.controlType == FeatureType.Decimal;
    },
    isString(): any {
      return this.controlType == FeatureType.String;
    },
    isDate(): any {
      return this.controlType == FeatureType.DateTime;
    },
    isEnum(): any {
      return this.controlType == FeatureType.Enum;
    },
    isCatalogue(): any {
      return this.controlType == FeatureType.Catalogue;
    },
    isDCatalogue(): any {
      return this.controlType == FeatureType.DCatalogue;
    },
    isDataField(): any {
      return this.controlType == FeatureType.DataField;
    },

    value: {
      get: function () {
        if (this.isInteger) return parseInt(this.modelLocal.value);
        else if (this.isDecimal) return parseFloat(this.modelLocal.value);
        else if (this.isDate && this.modelLocal.value) {
          return new Date(this.modelLocal.value);
        } else return this.modelLocal.value;
      },
      set: function (newValue) {
        if (newValue) {
          if (this.isDate) {
            let date = new Date(newValue);
            this.modelLocal.value = date.toISOString();
          } else this.modelLocal.value = newValue.toString();
        } else this.modelLocal.value = null;
      },
    },
  },
  data() {
    return {
      displayedOperations: [],
      modelLocal: {},
      controlType: FeatureType.String,
      controlData: {
        catalogueId: null,
        enumId: null,
        dCatalogueId: null,
        controlDescription: null,
        cataloguePath: null,
        enumType: null,
        dCatalogueModel: null,
      },
    };
  },
  methods: {
    async onChange(editMode: any) {
      const { data }: { data: any } = await getFeature(
        this.modelLocal.featureId
      );
      const info = data;
      this.displayedOperations = setDisplayedOperations(
        data.type as FeatureType
      );
      if (!editMode)
        this.modelLocal.operation = setOperation(data.type as FeatureType);
      this.setControlType(data);
      if (!editMode) this.resetValues();
      if (this.isCatalogue || this.isEnum || this.isDCatalogue)
        this.setValues(editMode, info);
    },

    async setValues(editMode: any, info: any) {
      if (editMode) {
        this.controlData.controlDescription = this.modelLocal.valueDescription;
        if (this.isCatalogue)
          this.controlData.catalogueId = parseInt(this.modelLocal.value);
        if (this.isEdit)
          this.controlData.enumId = parseInt(this.modelLocal.value);
        if (this.isDCatalogue)
          this.controlData.dCatalogueId = parseInt(this.modelLocal.value);
      }
      if (this.isCatalogue)
        this.controlData.cataloguePath =
          "catalogues/" + info.catalogueName + "/" + info.catalogueName;
      if (this.isEnum) {
        this.controlData.enumType = info.enumName;
        this.controlData.enumId = parseInt(this.modelLocal.value);
      }
      if (this.isDCatalogue) {
        const { data }: { data: any } = await getDCatalogue(info.dCatalogueId);
        this.controlData.dCatalogueModel = data;
      }
    },

    setControlType(data: any) {
      if (!data) return;
      const type = data.type as FeatureType;
      this.controlType = type;
    },

    resetValues() {
      this.value = null;
      this.modelLocal.valueDescription = null;
      this.controlData = {};
    },

    onCatalogueChange() {
      this.modelLocal.value = this.controlData.catalogueId.toString();
      this.modelLocal.valueDescription = this.controlData.controlDescription;
    },
    onEnumChange() {
      if (this.controlData.enumId) {
        this.modelLocal.value = this.controlData.enumId.toString();
        this.modelLocal.valueDescription = this.controlData.controlDescription;
      }
    },
    onDCatalogueChange() {
      if (this.controlData.dCatalogueId) {
        this.modelLocal.value = this.controlData.dCatalogueId.toString();
        this.modelLocal.valueDescription = this.controlData.controlDescription;
      }
    },

    dialog() {
      return this.$refs.dialog as any;
    },
    open(model: any, editType: any) {
      this.dialog().open(model, editType);
      this.modelLocal = this.dialog().createEditModel;
      this.controlType = null;
      if (editType == "edit") {
        this.onChange(true);
      }
    },
    close() {
      this.dialog().close();
    },
    save() {
      this.dialog().save();
    },
    saveData() {
      if (this.isBoolean) {
        if (this.modelLocal.value == "True")
          this.modelLocal.valueBoolean = true;
        if (this.modelLocal.value == "False")
          this.modelLocal.valueBoolean = false;
      }
      if (this.isDate) {
        this.modelLocal.valueDateTime = new Date(this.modelLocal.value);
      }
      if (!this.isEnum && !this.isCatalogue && !this.isDCatalogue) {
        this.modelLocal.valueDescription = this.modelLocal.value;
      }
    },
  },
});
